// src/components/Common/CookieConsent/CookieConsent.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCookie, FaTimes, FaCog } from 'react-icons/fa';
import styles from './CookieConsent.module.css';

const CookieConsent = () => {
  const { t } = useTranslation(['common']);
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('adtac-cookie-consent');
    if (!consent) {
      // Show banner after 2 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('adtac-cookie-consent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now()
    }));
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('adtac-cookie-consent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now()
    }));
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowDetails(!showDetails);
  };

  if (!isVisible) return null;

  return (
    <div className={styles.cookieConsent}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.icon}>
            <FaCookie />
          </div>
          
          <div className={styles.textContent}>
            <h3 className={styles.title}>Cookie Preferences</h3>
            <p className={styles.description}>
              We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. 
              Our analytics help us improve our food safety training services for you.
            </p>
            
            {showDetails && (
              <div className={styles.cookieDetails}>
                <div className={styles.cookieCategory}>
                  <h4>Essential Cookies</h4>
                  <p>Required for website functionality and cannot be disabled. These enable core website features.</p>
                </div>
                
                <div className={styles.cookieCategory}>
                  <h4>Analytics Cookies</h4>
                  <p>Help us understand how visitors interact with our website by collecting and reporting anonymous information.</p>
                </div>
                
                <div className={styles.cookieCategory}>
                  <h4>Marketing Cookies</h4>
                  <p>Used to track visitors for personalized advertising and marketing purposes.</p>
                </div>
              </div>
            )}
            
            <div className={styles.links}>
              <a 
                href="mailto:info@adtac.co?subject=Privacy%20Policy%20Inquiry" 
                className={styles.privacyLink}
                aria-label="Contact us about our Privacy Policy and data protection practices"
              >
                Privacy Policy Inquiry
              </a>
              <a 
                href="mailto:info@adtac.co?subject=Cookie%20Policy%20Inquiry" 
                className={styles.cookieLink}
                aria-label="Contact us to learn about our Cookie Policy and data usage"
              >
                Cookie Policy Inquiry
              </a>
            </div>
          </div>
          
          <div className={styles.actions}>
            <button 
              className={styles.customizeButton}
              onClick={handleCustomize}
              aria-label="Customize cookie preferences and learn about different cookie types"
            >
              <FaCog /> Customize
            </button>
            
            <button 
              className={styles.acceptNecessaryButton}
              onClick={handleAcceptNecessary}
              aria-label="Accept only essential cookies required for website functionality"
            >
              Accept Necessary Only
            </button>
            
            <button 
              className={styles.acceptAllButton}
              onClick={handleAcceptAll}
              aria-label="Accept all cookies including analytics and marketing for enhanced user experience"
            >
              Accept All Cookies
            </button>
            
            <button 
              className={styles.closeButton}
              onClick={() => setIsVisible(false)}
              aria-label="Close cookie consent banner"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;